import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSliderDragEvent } from '@angular/material/slider';
import { LayoutService, SnackbarService } from '@core/services';
import { ProfileService } from '@core/services/profile.service';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { indicate, keepDialogOpen } from '@shared/helpers';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform } from 'ngx-image-cropper';
import { Observable, Subject } from 'rxjs';

@Component({
    selector: 'app-change-image',
    templateUrl: './change-image.component.html',
    styleUrls: ['./change-image.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChangeImageComponent implements OnInit, AfterViewInit {
  static readonly componentName = 'ChangeImageComponent';
  
  @ViewChild('imgFileInput') input: ElementRef;
  @ViewChild(ImageCropperComponent, { read: ElementRef }) private cropper: ElementRef<HTMLElement>;

  public position = position;
  public onDragEnded = onDragEnded;

  croppedImage: any = '';
  imageChangedEvent: any = '';
  isSubmitting$ = new Subject<boolean>();
  isMobile$: Observable<boolean>;
  scale = 1;
  transform: ImageTransform = {};

  constructor(
    @Inject(MAT_DIALOG_DATA) public sysText: any,
    private dialogRef: MatDialogRef<ChangeImageComponent>,
    private profileService: ProfileService,
    private layoutService: LayoutService,
    private snackbarService: SnackbarService
  ) { }

  ngOnInit() {
    this.isMobile$ = this.layoutService.isMobile$();
  }
  ngAfterViewInit() {
    this.input.nativeElement.click();
  }

  fileChangeEvent(event: any): void {
    if (event.target.files[0]) {
      this.imageChangedEvent = event;
    }
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64;
  }

  // the library's crop box has no role, so NVDA keeps the arrow keys in browse mode; it's drawn on the next render
  onCropperReady(): void {
    requestAnimationFrame(() => this.cropper.nativeElement.querySelector('.ngx-ic-cropper')?.setAttribute('role', 'application'));
  }

  loadImageFailed() {
    this.snackbarService
      .open('Image failed to load. Please try again', 'Retry', 5000)
      .onAction()
      .subscribe(() => this.input.nativeElement.click());
  }

  zoomOut() {
    if (this.scale > 0.1) {
      this.scale -= 0.1;
      this.transform = {
        ...this.transform,
        scale: this.scale
      };
    }
  }

  zoomIn() {
    if (this.scale < 2) {
      this.scale += 0.1;
      this.transform = {
        ...this.transform,
        scale: this.scale
      };
    }
  }

  zoom(event: MatSliderDragEvent): void {
    this.transform = {
      ...this.transform,
      scale: this.scale
    };
  }

  save() {
    // the cropper's [disabled] doesn't block its arrow-key moves, so return the image that was actually saved, not a later crop
    const image = this.croppedImage;
    this.profileService
      .changeProfileImage(image)
      .pipe(
        indicate(this.isSubmitting$),
        keepDialogOpen(this.dialogRef)
      )
      .subscribe(() => this.dialogRef.close(image));
  }
}
